import { CareerSkillEdgeKind } from 'core/hooks/CareerSkillGraph/transform/transformCareerSkillGraph';

import { IPlacedCard, ISkillLayout } from './skillGraphLayout';
import { ISkillGraphIndex, neighbourhoodOf } from './skillGraphModel';

/**
 * Геометрия связей карты (ТЗ 0070).
 *
 * Зависимости идут МЕЖДУ навыками разных колонок — это направленный граф
 * поверх дерева, и без мер против месива он превращается в паутину, из которой
 * читатель не достаёт ничего.
 *
 * Шесть мер, по убыванию эффекта:
 *
 * 1. **Выключены по умолчанию.** Переключатель `Zależności` — это решение
 *    человека принять больше линий на экране.
 * 2. **Отдельный слой ПОД узлами** — линия никогда не перечёркивает подпись.
 * 3. 🔴 **Подсветка окрестности выбранного.** Всё, что дальше одного шага,
 *    гасится до 12 %. Десять строк, которые дают больше любой хитрой
 *    маршрутизации: читатель видит не «все связи», а «связи вот этого».
 * 4. **Обратное ребро обходит полосу узлов** сверху или снизу — тем краем, что
 *    ближе, — а не проходит сквозь коробки.
 * 5. **Потолок в сорок рёбер.** Выше — только окрестность выбора, и легенда
 *    говорит об этом словами. Нарисовать двести линий, в которых никто не
 *    разберётся, хуже, чем честно назвать предел.
 * 6. **Список всегда называет предпосылки текстом** — это и читаемое
 *    представление, и заодно доступное.
 */

export const MAX_DEP_EDGES = 40;
export const DIM_OPACITY = 0.12;

export interface IEdgePath {
  key: string;
  kind: CareerSkillEdgeKind;
  d: string;
  dimmed: boolean;
}

/** Детерминированный хеш пары: одинаковые рёбра всегда расходятся одинаково. */
const hash = (value: string): number => {
  let acc = 0;
  for (let i = 0; i < value.length; i += 1) {
    acc = (acc * 31 + value.charCodeAt(i)) % 100003;
  }
  return acc;
};

const rightEdge = (row: IPlacedCard) => ({ x: row.x + row.w, y: row.y + row.h / 2 });
const leftEdge = (row: IPlacedCard) => ({ x: row.x, y: row.y + row.h / 2 });

/** Иерархия — прямоугольный локоть в промежутке между колонками. */
export const hierarchyPath = (parent: IPlacedCard, child: IPlacedCard): string => {
  const from = rightEdge(parent);
  const to = leftEdge(child);
  const mid = from.x + (to.x - from.x) / 2;
  return `M ${from.x} ${from.y} H ${mid} V ${to.y} H ${to.x}`;
};

/**
 * Зависимость — кубическая кривая с детерминированным смещением опорных точек,
 * чтобы параллельные связи не сливались в одну линию.
 */
export const dependencyPath = (
  source: IPlacedCard,
  target: IPlacedCard,
  band: { top: number; bottom: number },
): string => {
  const from = rightEdge(source);
  const to = leftEdge(target);
  const spread = 24 + (hash(`${source.key}->${target.key}`) % 40);

  if (to.x > from.x) {
    return `M ${from.x} ${from.y} C ${from.x + spread} ${from.y}, ${to.x - spread} ${to.y}, ${
      to.x
    } ${to.y}`;
  }

  // Обратное ребро: обходим полосу узлов тем краем, что ближе. Пройти сквозь
  // коробки значило бы нарисовать линию поверх подписей, которые она связывает.
  const middle = (from.y + to.y) / 2;
  const detour =
    middle - band.top < band.bottom - middle
      ? band.top - 32 - (spread % 24)
      : band.bottom + 32 + (spread % 24);
  return (
    `M ${from.x} ${from.y} C ${from.x + spread} ${from.y}, ${from.x + spread} ${detour}, ` +
    `${(from.x + to.x) / 2} ${detour} C ${to.x - spread} ${detour}, ${to.x - spread} ${to.y}, ${
      to.x
    } ${to.y}`
  );
};

export const buildEdgePaths = (
  index: ISkillGraphIndex,
  layout: ISkillLayout,
  options: { deps: boolean; selected: string | null },
): { hierarchy: IEdgePath[]; dependencies: IEdgePath[]; capped: boolean } => {
  const hierarchy: IEdgePath[] = [];
  layout.cards.forEach((row) => {
    if (!row.parentKey) return;
    const parent = layout.byKey.get(row.parentKey);
    if (!parent) return;
    hierarchy.push({
      key: `h:${parent.key}->${row.key}`,
      kind: 'hierarchy',
      d: hierarchyPath(parent, row),
      dimmed: false,
    });
  });

  if (!options.deps) return { hierarchy, dependencies: [], capped: false };

  const spotlight = neighbourhoodOf(index, options.selected);
  const band = layout.cards.reduce(
    (acc, row) => ({
      top: Math.min(acc.top, row.y),
      bottom: Math.max(acc.bottom, row.y + row.h),
    }),
    { top: Number.POSITIVE_INFINITY, bottom: 0 },
  );

  const all: IEdgePath[] = [];
  index.opensOut.forEach((edges) => {
    edges.forEach((edge) => {
      const source = layout.byKey.get(edge.from);
      const target = layout.byKey.get(edge.to);
      // Один из концов не нарисован — связи на карте нет. Список предпосылок в
      // паспорте её всё равно назовёт словами.
      if (!source || !target) return;
      const inSpotlight = spotlight.has(edge.from) && spotlight.has(edge.to);
      all.push({
        key: `d:${edge.from}->${edge.to}`,
        kind: edge.kind,
        d: dependencyPath(source, target, band),
        dimmed: spotlight.size > 0 && !inSpotlight,
      });
    });
  });

  if (all.length <= MAX_DEP_EDGES) {
    return { hierarchy, dependencies: all, capped: false };
  }
  // 🔴 Выше потолка БЕЗ выбранного узла не рисуем ничего. Соблазн «показать
  // всё, раз выбирать нечего» даёт ровно ту паутину, ради которой потолок и
  // заводился: сорок с лишним линий поверх двенадцати коробок не читаются
  // никак. Легенда в этом случае говорит словами: выберите навык.
  const near = all.filter((row) => !row.dimmed);
  return {
    hierarchy,
    dependencies: options.selected ? near : [],
    capped: true,
  };
};
